// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Main Layout with Sidebar
// ═══════════════════════════════════════════════════════════

import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import { useTranslation } from 'react-i18next';
import { store, logout } from '@/lib/store';
import { cn } from '@/lib/utils';
import {
  FolderOpen,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff,
  ShieldCheck,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { t } = useTranslation();
  const snap = useSnapshot(store);
  const location = useLocation();
  const navigate = useNavigate();
  const collapsed = snap.sidebarCollapsed;

  const navItems = [
    { to: '/projects', icon: FolderOpen, label: t('nav.projects') },
    { to: '/settings', icon: Settings, label: t('nav.settings') },
    // Admin nav — only visible for admin/cto roles
    ...(snap.user?.role === 'admin' || snap.user?.role === 'cto'
      ? [{ to: '/admin', icon: ShieldCheck, label: t('nav.admin') }]
      : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 72 : 260 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        className="flex flex-col bg-surface-900/80 backdrop-blur-xl border-r border-surface-800/60 relative z-10"
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-surface-800/60">
          <div className="flex items-center gap-3">
            <img
              src="/arams-logo-white.png"
              alt={t('common.appName')}
              className="w-9 h-9 rounded-xl object-contain flex-shrink-0"
            />
            <AnimatePresence>
              {!collapsed && (
                <motion.div
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  className="overflow-hidden whitespace-nowrap"
                >
                  <span className="font-bold text-lg gradient-text">{t('common.appName')}</span>
                  <span className="text-xs text-surface-500 block -mt-1">
                    {t('common.tagline')}
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 py-4 px-3 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname.startsWith(item.to);
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group',
                  isActive
                    ? 'bg-brand-600/20 text-brand-400 border border-brand-500/20'
                    : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50',
                )}
              >
                <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-brand-400')} />
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-sm font-medium"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="p-3 border-t border-surface-800/60 space-y-2">
          {/* Connection Status */}
          <div
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-xs',
              snap.wsConnected ? 'text-success' : 'text-surface-500',
            )}
          >
            {snap.wsConnected ? (
              <Wifi className="w-3.5 h-3.5" />
            ) : (
              <WifiOff className="w-3.5 h-3.5" />
            )}
            {!collapsed && (
              <span>{snap.wsConnected ? t('common.connected') : t('common.disconnected')}</span>
            )}
          </div>

          {/* User */}
          {snap.user && (
            <div className="flex items-center gap-3 px-3 py-2 rounded-lg">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-purple-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {snap.user.name.charAt(0).toUpperCase()}
              </div>
              {!collapsed && (
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{snap.user.name}</div>
                  <div className="text-xs text-surface-500 truncate">{snap.user.role}</div>
                </div>
              )}
            </div>
          )}

          {/* Logout */}
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-surface-400 hover:text-danger hover:bg-danger/10 transition-all w-full"
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="text-sm">{t('auth.logout')}</span>}
          </button>

          {/* Toggle */}
          <button
            onClick={() => (store.sidebarCollapsed = !store.sidebarCollapsed)}
            className="flex items-center justify-center w-full py-1.5 rounded-lg text-surface-500 hover:text-surface-300 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto bg-surface-950">
        <div className="min-h-full">{children}</div>
      </main>
    </div>
  );
}
